import React, { useState, useEffect, useRef } from 'react';
import { Button, TextField, IconButton } from '@mui/material';
import { Tooltip } from '@mui/material';
import { CircleUserRound, Moon, Sun, ChevronDown } from 'lucide-react';
import { Bell } from 'lucide-react';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { useNavigate, Link } from 'react-router-dom';
import '../../css/HRPortal.css';
import axios from 'axios';
import host from '../../global';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { toast } from 'react-toastify';
import { signOut, getCurrentUser } from '../../utils/authSession';
import { identifyUser, setCompany } from '../../utils/analytics';
import { LogOut, UserPen, Search, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleThemeReducer } from '../../store/slices/miscSlice';
import { useTheme } from '../../hooks/useTheme';
import {
  clearCandidateManagementSlice,
  updateActiveTab,
  updateNavigationTab,
} from '../../store/slices/candidateManagementSlice';
import { clearInterviewManagementSlice } from '../../store/slices/InterviewManagementSlice';
import {
  clearFilterSlice,
  removeActiveFilter,
  toggleFilterDrawer,
  toggleFilterDropdown,
  updateCurrentJobRole,
} from '../../store/slices/filterOptionsSlice';
import {
  clearUserSlice,
  updateCurrentOrgId,
} from '../../store/slices/userSlice';
import { useSourcingConfig } from '../../contexts/SourcingConfigContext';

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { sourcingEnabled } = useSourcingConfig();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [notification, setNotification] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const [isMCQDropdownOpen, setIsMCQDropdownOpen] = useState(false);
  const [isAnalyticsDropdownOpen, setIsAnalyticsDropdownOpen] = useState(false);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const accessToken = sessionStorage.getItem('accessToken');
  const popupRef = useRef(null);
  const [userData, setUserData] = useState(null);
  const [userId, setUserId] = useState(null);
  const dispatch = useDispatch();
  const navigationTab = useSelector(
    (state) => state.candidateManagementSlice.navigationTab,
  );
  const { guardedNavigate, disableGuard } = useNavigationGuard(); // Get guardedNavigate from context

  const availablePages = [
    { name: 'Home', route: '/org-admin/dashboard' },
    { name: 'Candidate Reports', route: '/org-admin/candidatereports' },
    { name: 'Candidate Analytics', route: '/org-admin/candidateanalytics' },
    { name: 'Recruiter Analytics', route: '/org-admin/recruiter-analytics' },
    { name: 'Edit Profile', route: '/org-admin/editprofile' },
    { name: 'Analyze Resume', route: '/org-admin/checkresume' },
    { name: 'Create Job Role', route: '/org-admin/create-jd-smart' },
    { name: 'Team', route: '/org-admin/hrprofiles' },
    { name: 'Jobs', route: '/org-admin/created-jds' },
    { name: 'Test Set', route: '/org-admin/testlibrary' },
  ];
  // const [isTicketButtonHovered, setIsTicketButtonHovered] = useState(false);
  // const ticketModalRef = useRef(null);
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (
  //       isTicketModalOpen &&
  //       ticketModalRef.current &&
  //       !ticketModalRef.current.contains(event.target)
  //     ) {
  //       setIsTicketModalOpen(false);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [isTicketModalOpen]);
  const updateLastLogin = async () => {
    try {
      const response = await axios.post(
        `${host}/update_last_login`,
        {
          timestamp: new Date().toISOString(), // Send current system time in body
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Send token in headers (correct location)
          },
        },
      );
      dispatch(updateCurrentOrgId({ org_id: response.data.org_id, org_admin_id: response.data.org_admin_id }));
      setCompany(response.data.org_id);
    } catch (error) {
      console.error('Error updating last login', error);
      // Silent fail — this is a background task
    }
  };

  const fetchProfileData = async () => {
    try {
      const { userId } = await getCurrentUser();
      setUserId(userId);
      const response = await axios.get(`${host}/getadmindetails`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setUserData(response.data);
      // Runs on every login and every app load with a session
      identifyUser(userId, {
        email: response.data.email,
        name: response.data.name,
        phone: response.data.contact,
        waOptIn: response.data.whatsapp_opt_in,
      });

      if (response.data.name) {
        localStorage.setItem('username', response.data.name);
      }
    } catch (error) {
      console.error('Error fetching Profile Data', error);
    }
  };
  useEffect(() => {
    fetchProfileData();
  }, [accessToken]);

  useEffect(() => {
    if (userId && accessToken) {
      updateLastLogin();
    }
  }, [userId, accessToken]);

  //for cleaning up states in redux
  useEffect(() => {
    // dispatch(updateNavigationTab('ALL'));
    return () => {
      // dispatch(updateNavigationTab('ALL'));
      dispatch(clearUserSlice());
    };
  }, []);

  const handleNotificationClick = (upload_id) => {
    navigate(`/org-admin/activity`, { state: { upload_id: upload_id } });
    setIsPopupVisible(false);
  };

  const handleSignOut = async () => {
    try {
      disableGuard();
      await signOut();
      dispatch(clearCandidateManagementSlice());
      dispatch(clearInterviewManagementSlice({ clearJobRoles: true }));
      dispatch(clearFilterSlice());
      dispatch(updateNavigationTab('ALL'));
      dispatch(clearUserSlice());
      Object.keys(localStorage).forEach((key) => {
        if (key !== 'username') {
          localStorage.removeItem(key);
        }
      });
      navigate('/');
    } catch (error) {
      console.error('Error signing out', error);
      toast.error('Error signing out');
    }
  };
  const profileImageUrl = userData?.profile
    ? `${process.env.REACT_APP_PROFILE_PHOTOS_HOST}/OrganizationAdmins/${userId}_${userData.profile}`
    : '';
  // const toggleTicketModal = () => setIsTicketModalOpen(!isTicketModalOpen);
  // const handleSubmit = async () => {
  //   try {
  //     const response = await axios.post(
  //       `${host}/raiseticket`,
  //       {},
  //       {
  //         params: { title: ticketTitle, description: ticketDescription },
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${accessToken}`,
  //         },
  //       }
  //     );
  //     if (response.status === 200) {
  //       toast.success("Ticket Submitted Successfully");
  //       setTicketTitle("");
  //       setTicketDescription("");
  //       toggleTicketModal();
  //     } else {
  //       toast.error("Error submitting ticket:", response.statusText);
  //     }
  //   } catch (error) {
  //     if (error.response) {
  //       const status = error.response.status;
  //       const detail = error.response.data.detail;
  //       if (status === 401) {
  //         await new Promise((resolve) => {
  //           Cookies.remove("accessToken");
  //           resolve();
  //         });
  //         toast.error(`Unauthorized: ${detail}`);
  //       } else if (status === 404) {
  //         toast.warning(`Not Found: ${detail}`);
  //       } else {
  //         toast.error(`Error: ${detail}`);
  //       }
  //     } else {
  //       toast.error("An unexpected error occurred. Please try again.");
  //     }
  //   }
  // };
  const handleSearchInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query) {
      const filteredSuggestions = availablePages.filter((page) =>
        page.name.toLowerCase().includes(query.toLowerCase()),
      );
      setSuggestions(filteredSuggestions);
      setNoResults(filteredSuggestions.length === 0);
    } else {
      setSuggestions([]);
      setNoResults(false);
    }
  };

  const handleMouseEnter = () => {
    setIsPopupVisible(true);
  };
  const handleMouseLeave = () => {
    setIsPopupVisible(false);
  };
  const handleSuggestionClick = (route) => {
    navigate(route);
    setSearchQuery('');
    setSuggestions([]);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsPopupVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isActionsDropdownOpen && !event.target.closest('.dropdown')) {
        setIsActionsDropdownOpen(false);
      }
      if (isMCQDropdownOpen && !event.target.closest('.mcqdropdown-menu')) {
        setIsMCQDropdownOpen(false);
      }
      if (isAnalyticsDropdownOpen && !event.target.closest('.analytics-dropdown')) {
        setIsAnalyticsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionsDropdownOpen, isMCQDropdownOpen, isAnalyticsDropdownOpen]);

  useEffect(() => {
    const fetchBulkResumeUploadResult = async () => {
      try {
        const response = await axios.get(`${host}/resumes/bulk`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            jd_id: 'all',
          },
        });
        const results = response.data.notifications || [];
        results.sort((a, b) => {
          if (a.bulk_upload_date === null && b.bulk_upload_date === null)
            return 0;
          if (a.bulk_upload_date === null) return 1;
          if (b.bulk_upload_date === null) return -1;
          return new Date(b.bulk_upload_date) - new Date(a.bulk_upload_date);
        });
        setNotification(results);
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            await new Promise((resolve) => {
              sessionStorage.removeItem('accessToken');
              resolve();
            });
            toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
            toast.warning(`Not Found: ${detail}`);
          } else {
            toast.error(`Error: ${detail}`);
          }
        } else {
          toast.error('An unexpected error occurred. Please try again.');
        }
      }
    };
    fetchBulkResumeUploadResult();
  }, [accessToken]);

  const fetchPendingAccountRequest = async () => {
    try {
      const response = await axios.get(`${host}/pendingaccounts`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setNotification(response.data.results);
    } catch (error) {
      console.error('Error fetching Profile Data', error);
    }
  };

  return (
    <>
      <header className="main-header grad dark:bg-transparent bg-bgPrimary  flex justify-between items-center pt-3 pb-1 px-8 gap-4 flex-nowrap dark:box-border border-b dark:border-white/10 border-gray-200 transition-colors">
        <nav className="flex flex-wrap items-center min-w-0 flex-1">
          {/* <button
            onClick={() => navigate(`/org-admin/dashboard`)}
            className={`${location.pathname.includes('dashboard')
              ? 'text-transparent bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text relative'
              : 'text-white'
              }`}
          >
            Home
            {location.pathname.includes('dashboard') && (
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/5 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500"></span>
            )}
          </button>

          <button
            onClick={() => navigate(`/org-admin/candidatereports`)}
            className={`${location.pathname.includes('candidatereports')
              ? 'text-transparent bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text relative'
              : 'text-white'
              }`}
          >
            Candidate Reports
            {location.pathname.includes('candidatereports') && (
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4/5 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500"></span>
            )}
          </button>
          <button
            onClick={() => navigate(`/org-admin/created-jds`)}
            className={`${location.pathname.includes('created-jds')
              ? 'text-transparent bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text relative'
              : 'text-white'
              }`}
          >
            Job Library
            {location.pathname.includes('created-jds') && (
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4/5 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500"></span>
            )}
          </button>
      <button onClick={() => navigate("/org-admin/testlibrary")}
        className={`${(location.pathname.includes('testlibrary') || location.pathname.includes('createmcqset') || location.pathname.includes('createcodingproblemset'))
          ? 'text-transparent bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text relative'
          : 'text-white'
          }`}>
        Test Library
        {(location.pathname.includes('testlibrary') || location.pathname.includes('createmcqset') || location.pathname.includes('createcodingproblemset')) && (
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4/5 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500"></span>
        )}
      </button> */}

          <>
            <button
              onClick={() => {
                dispatch(updateNavigationTab('ALL'));
                guardedNavigate('/org-admin/jd-selection');
              }}
              data-text="Create Job Role"
              data-guide="create-job"
              className={`relative block text-base
              ${
                location.pathname.includes('jd-selection') ||
                location.pathname.includes('create-jd-smart')
                  ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                  : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
              }

              before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
            `}
            >
              Create Job Role
              {(location.pathname.includes('jd-selection') ||
                location.pathname.includes('create-jd-smart')) && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button>
            {sourcingEnabled && (
              <button
                onClick={() => {
                  dispatch(updateNavigationTab('ALL'));
                  guardedNavigate('/org-admin/find-candidates');
                }}
                data-text="Find Candidates"
                className={`relative block text-base
                ${
                  location.pathname.includes('find-candidates')
                    ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                    : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                }

                before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
              `}
              >
                Find Candidates
                {location.pathname.includes('find-candidates') && (
                  <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
                )}
              </button>
            )}
            <button
              onClick={() => {
                dispatch(updateNavigationTab('ALL'));
                guardedNavigate('/org-admin/checkresume');
              }}
              data-text="Screen a Resume"
              data-guide="screen-resume"
              className={`relative block text-base
              ${
                location.pathname.includes('checkresume')
                  ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                  : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
              }

              before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
            `}
            >
              Screen a Resume
              {location.pathname.includes('checkresume') && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button>
            <button
              onClick={() => {
                dispatch(updateCurrentJobRole('All Job Roles'));
                dispatch(removeActiveFilter('JOB_ROLE'));
                dispatch(updateNavigationTab('SOURCED'));
                guardedNavigate('/org-admin/schedule-interview');
              }}
              data-text="Screened Candidates"
              data-guide="screened-candidates"
              className={`relative block text-base
                ${
                  navigationTab === 'SOURCED'
                    ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                    : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                }

                before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
              `}
            >
              Screened Candidates
              {navigationTab === 'SOURCED' && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button>
            {/* <button
              onClick={() => guardedNavigate('/org-admin/job-applicants')}
              data-text="Screened Candidates"
              className={`relative block text-base
    ${
      location.pathname.includes('job-applicants')
        ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
        : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
    }

    before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
  `}
            >
              Screened Candidates
              {location.pathname.includes('job-applicants') && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button> */}
            <button
              onClick={() => {
                dispatch(updateCurrentJobRole('All Job Roles'));
                dispatch(removeActiveFilter('JOB_ROLE'));
                dispatch(updateNavigationTab('INTERVIEW'));
                guardedNavigate('/org-admin/candidate-management');
              }}
              data-text="Interview Management"
              data-guide="interview-management"
              className={`relative block text-base
                ${
                  navigationTab === 'INTERVIEW'
                    ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                    : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                }

                before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
              `}
            >
              Interview Management
              {navigationTab === 'INTERVIEW' && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button>

            {/* <button
              onClick={() => guardedNavigate("/org-admin/candidatereports")}
              data-text="Scheduled Interviews"
              className={`relative block text-base
                ${
                  location.pathname.includes("candidatereports")
                    ? "font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent"
                    : "text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]"
                }

                before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
              `}
            >
              Scheduled Interviews

              {location.pathname.includes("candidatereports") && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button> */}

            <div className="analytics-dropdown relative inline-block">
              <button
                onClick={() => setIsAnalyticsDropdownOpen((prev) => !prev)}
                data-text="Analytics"
                className={`relative block text-base
    ${
      location.pathname.includes('candidateanalytics') ||
      location.pathname.includes('recruiter-analytics')
        ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
        : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
    }

    before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
  `}
              >
                <span className="inline-flex items-center gap-1">
                  Analytics
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${isAnalyticsDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </span>
                {(location.pathname.includes('candidateanalytics') ||
                  location.pathname.includes('recruiter-analytics')) && (
                  <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
                )}
              </button>
              {isAnalyticsDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 rounded-xl shadow-2xl border border-gray-200 dark:border-white/10 bg-bgPrimary dark:bg-[#1a1f2e] overflow-hidden !z-[99999]">
                  <button
                    onClick={() => {
                      dispatch(updateNavigationTab('ALL'));
                      guardedNavigate('/org-admin/recruiter-analytics');
                      setIsAnalyticsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      location.pathname.includes('recruiter-analytics')
                        ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                        : 'text-textPrimary dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                  >
                    Recruiter Analytics
                  </button>
                  <button
                    onClick={() => {
                      dispatch(updateNavigationTab('ALL'));
                      guardedNavigate('/org-admin/candidateanalytics');
                      setIsAnalyticsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      location.pathname.includes('candidateanalytics')
                        ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                        : 'text-textPrimary dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                  >
                    Candidate Analytics
                  </button>
                </div>
              )}
            </div>
          </>
        </nav>
        <div className="main-header-actions flex items-center gap-4 shrink-0">
          <div className="group relative ml-2">
            <div className="group cursor-pointer">
              <img
                className="w-[27px] h-[27px] rounded-full border-1 border-white group-hover:hidden transition-all"
                src="/profileheader.png"
                alt="Profile"
              />
              <img
                className="w-[27px] h-[27px] rounded-full border-1 border-white hidden group-hover:block transition-all"
                src="/profileheaderhover.png"
                alt="Profile Hover"
              />
            </div>
            <div className="hidden group-hover:flex group-hover:items-start absolute top-6 right-0 bg-none  p-1 flex-col !z-[99999]">
              <div className="w-[200px] rounded-2xl shadow-2xl overflow-hidden border border-gray-500 dark:border-purple-300 ">
                <div className="relative ">
                  <div className="absolute inset-0 dark:opacity-75 opacity-50"></div>
                  {userData ? (
                    <div className="dark:bg-gradient-to-br dark:from-[rgba(34,52,97,0.25)] dark:to-[rgba(11,10,31,0.05)] dark:backdrop-blur-[50px] bg-bgPrimary ">
                      <div className="relative z-10 flex flex-col sm:flex-col items-center p-2">
                        {profileImageUrl ? (
                          <img
                            className="w-[110px] h-[110px] rounded-full border-4 dark:border-white border-gray-400 shadow-lg object-cover"
                            src={profileImageUrl}
                            alt={
                              userData?.name
                                ? userData.name[0].toUpperCase()
                                : 'Profile Image'
                            }
                          />
                        ) : (
                          <img
                            className="w-[90px] h-[90px] rounded-full border-1 border-white shadow-lg object-cover p-4 mb-2"
                            src="/profileheaderhover.png"
                          />
                        )}
                        <div className="flex flex-col gap-2 justify-center items-center">
                          <h2 className="text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-blue-500">
                            {userData?.name || 'No Name Available'}
                          </h2>
                          <p className="text-xs dark:text-gray-300 text-textSecondary font-normal -mt-2">
                            Organization Admin
                          </p>
                          <div className="flex justify-center sm:justify-start gap-3 p-2 mb-2">
                            <UserPen
                              className="text-blue-600 bg-blue-100 rounded-full p-2 w-8 h-8 hover:bg-blue-200 hover:text-blue-700 transition-all duration-300 cursor-pointer"
                              onClick={() =>
                                guardedNavigate(`/org-admin/editprofile`)
                              }
                            />
                            <LogOut
                              className="text-red-600 bg-red-100 rounded-full p-2 w-8 h-8 hover:bg-red-200 hover:text-red-700 transition-all duration-300 cursor-pointer"
                              onClick={handleSignOut}
                            />
                            <button
                              onClick={toggleTheme}
                              className="rounded-full p-2 w-8 h-8 dark:bg-gray-800 bg-yellow-200 text-textPrimary transition-colors flex items-center justify-center"
                            >
                              {theme === 'dark' ? (
                                <Moon className="w-5 h-5 text-gray-200" />
                              ) : (
                                <Sun className="w-5 h-5 text-yellow-600" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div class="relative flex w-64 animate-pulse gap-2 p-4">
                      <div class="h-12 w-12 rounded-full dark:bg-slate-400 bg-gray-200"></div>
                      <div class="flex-1">
                        <div class="mb-1 h-5 w-3/5 rounded-lg dark:bg-slate-400 bg-gray-200 text-lg"></div>
                        <div class="h-5 w-[90%] rounded-lg dark:bg-slate-400 bg-gray-200 text-sm"></div>
                      </div>
                      <div class="absolute bottom-5 right-0 h-4 w-4 rounded-full dark:bg-slate-400 bg-gray-200"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
export default Header;
